import { type CSSProperties, type JSX, useMemo } from 'react';

export interface HeroRichTextProps {
  /**
   * Tiptap ProseMirror JSON serialized as a string. Schema-capped at
   * 20 KB so the per-render parse + render cost stays bounded.
   */
  contentJson: string;
  /** Optional hero background image (Pro). */
  imageSrc?: string;
}

/**
 * Rich-text hero — renders Builder-authored content (headings, lists,
 * emphasis, links) inside the hero slab. Receives ProseMirror JSON
 * (the same shape Tiptap produces) and walks the tree to HTML with a
 * minimal node + mark whitelist, so the launcher does NOT carry a
 * Tiptap dependency at runtime — Tiptap lives in the Builder side
 * where the authoring happens.
 *
 * The whitelist covers what Tiptap's StarterKit + Link emits: doc,
 * paragraph, heading (h1-h3), bulletList / orderedList / listItem,
 * blockquote, codeBlock, horizontalRule, hardBreak, text. Marks: bold,
 * italic, strike, code, link. Anything outside the whitelist renders
 * as plain text — never as raw HTML — so a malformed/malicious config
 * can't inject script.
 */
export function HeroRichText({ contentJson, imageSrc }: HeroRichTextProps): JSX.Element {
  const html = useMemo(() => {
    try {
      const json = JSON.parse(contentJson) as PMNode;
      return renderNode(json);
    } catch {
      // Surface parse failures as a visible note rather than a silent
      // blank slab so the admin notices something's wrong with the
      // shipped config.
      return null;
    }
  }, [contentJson]);

  const style: CSSProperties | undefined =
    imageSrc !== undefined
      ? {
          backgroundImage:
            `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.45) 100%), url(${imageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : undefined;

  return (
    <section
      className={`launcher-hero launcher-hero--richtext${
        imageSrc !== undefined ? ' launcher-hero--has-image' : ''
      }`}
      {...(style && { style })}
      data-testid="launcher-hero"
      data-hero-kind="richText"
    >
      {html === null ? (
        <p className="launcher-hero__richtext-error" role="alert">
          Hero content failed to render.
        </p>
      ) : (
        <div
          className="launcher-hero__richtext"
          data-testid="launcher-hero-richtext"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </section>
  );
}

interface PMNode {
  type: string;
  content?: PMNode[];
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  attrs?: Record<string, unknown>;
}

// Tiptap StarterKit + Link node/mark types we accept. Anything else
// falls through to plain-text — never raw HTML — so a malformed or
// hostile config can't inject script tags.
const ALLOWED_NODES = new Set([
  'doc',
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'horizontalRule',
  'hardBreak',
  'text',
  // Image insert (Tiptap Image extension). `src` is sanitized to safe
  // protocols only — data: URLs are accepted (the Builder embeds
  // uploaded images as base64) and http(s) URLs pass through; anything
  // else (javascript:, file:, etc.) drops to an empty node.
  'image',
]);
const ALLOWED_MARKS = new Set([
  'bold', 'italic', 'strike', 'code', 'link',
  // textStyle is the base mark for Color; carries `color` attr.
  'textStyle',
]);

function renderNode(node: PMNode | undefined): string {
  if (node === undefined || !ALLOWED_NODES.has(node.type)) return '';
  const children = (node.content ?? []).map(renderNode).join('');
  // textAlign is an attribute Tiptap's TextAlign extension sets on
  // paragraph + heading nodes. Whitelist the four values and emit
  // an inline style — using an inline style (not a class) keeps the
  // launcher's CSS free of doc-specific rules.
  const alignStyle = textAlignStyle(node.attrs?.['textAlign']);
  switch (node.type) {
    case 'doc':
      return children;
    case 'paragraph':
      return `<p${alignStyle}>${children}</p>`;
    case 'heading': {
      const level = clampLevel(node.attrs?.['level']);
      return `<h${String(level)}${alignStyle}>${children}</h${String(level)}>`;
    }
    case 'bulletList':
      return `<ul>${children}</ul>`;
    case 'orderedList':
      return `<ol>${children}</ol>`;
    case 'listItem':
      return `<li>${children}</li>`;
    case 'blockquote':
      return `<blockquote>${children}</blockquote>`;
    case 'codeBlock':
      return `<pre><code>${children}</code></pre>`;
    case 'horizontalRule':
      return '<hr />';
    case 'hardBreak':
      return '<br />';
    case 'text':
      return renderText(node);
    case 'image':
      return renderImage(node);
    default:
      return '';
  }
}

function textAlignStyle(value: unknown): string {
  if (typeof value !== 'string') return '';
  if (value === 'left' || value === 'center' || value === 'right' || value === 'justify') {
    return ` style="text-align:${value}"`;
  }
  return '';
}

/**
 * Image node — Tiptap's Image emits `{ type: 'image', attrs: { src,
 * alt?, title? } }`. We accept data: URLs (base64 from the Builder's
 * upload), https://, and http:// only. anything else (javascript:,
 * file:) drops to empty so a hostile payload can't smuggle JS in.
 */
function renderImage(node: PMNode): string {
  const src = typeof node.attrs?.['src'] === 'string' ? (node.attrs['src'] as string) : '';
  if (!isSafeImageSrc(src)) return '';
  const alt = typeof node.attrs?.['alt'] === 'string' ? (node.attrs['alt'] as string) : '';
  return `<img class="launcher-hero__richtext-img" src="${escapeAttr(src)}" alt="${escapeAttr(alt)}" loading="lazy" />`;
}

function isSafeImageSrc(src: string): boolean {
  if (src.startsWith('https://') || src.startsWith('http://')) return true;
  // data:image/* — the Builder's image uploader produces these. Any
  // other data: subtype (data:text/html, data:application/...) would
  // otherwise let an admin embed arbitrary content.
  if (src.startsWith('data:image/')) return true;
  return false;
}

function renderText(node: PMNode): string {
  const safe = escapeHtml(node.text ?? '');
  const marks = (node.marks ?? []).filter((m) => ALLOWED_MARKS.has(m.type));
  return marks.reduce((acc, mark) => wrapMark(acc, mark), safe);
}

function wrapMark(inner: string, mark: { type: string; attrs?: Record<string, unknown> }): string {
  switch (mark.type) {
    case 'bold':
      return `<strong>${inner}</strong>`;
    case 'italic':
      return `<em>${inner}</em>`;
    case 'strike':
      return `<s>${inner}</s>`;
    case 'code':
      return `<code>${inner}</code>`;
    case 'link': {
      const href = typeof mark.attrs?.['href'] === 'string' ? mark.attrs['href'] : '';
      if (!isSafeHref(href)) return inner;
      // rel + target neutralize tabnabbing on external links.
      return `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
    }
    case 'textStyle': {
      // Tiptap's Color extension stores `{ color: '#abc' | '#aabbcc' }`
      // on a textStyle mark. Only accept the strict hex shape — that
      // bounds the attack surface (no `color: red; background:
      // url(javascript:…)` smuggled in).
      const color = typeof mark.attrs?.['color'] === 'string' ? mark.attrs['color'] : '';
      if (!isSafeColor(color)) return inner;
      return `<span style="color:${color}">${inner}</span>`;
    }
    default:
      return inner;
  }
}

function clampLevel(raw: unknown): 1 | 2 | 3 {
  const n = typeof raw === 'number' ? Math.round(raw) : 0;
  if (n >= 3) return 3;
  if (n === 2) return 2;
  return 1;
}

// Refuse anything that's not http(s) or mailto. No javascript:, no
// data: — that's the entire attack surface for the link mark.
function isSafeHref(href: string): boolean {
  if (href.startsWith('http://') || href.startsWith('https://')) return true;
  if (href.startsWith('mailto:')) return true;
  return false;
}

/**
 * Strict 3-digit or 6-digit hex only. No `red`, `rgba(...)`, no `url(...)`
 * — the only CSS the color mark is allowed to emit is `color: #abc[def]`,
 * which bounds the attack surface to zero useful CSS injection.
 */
function isSafeColor(value: string): boolean {
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
